import React, { useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useLocation, useParams } from "react-router-dom";
import { getSiteById } from "../../services/siteServices/subSiteService";
import {
  createAssetBySiteId,
  deleteAssetById,
  getAssetStructureDesign,
} from "../../services/siteServices/assetsServices";
import { validateFields } from "../../helpers/utils";
import AppConfig from "../../config";
import DynamicTable from "./DynamicTable";
import SnackBars from "../snackbars/SnackBars";
import { Title } from "@mui/icons-material";
import { type } from "@testing-library/user-event/dist/type";

// Custom theme with reduced font size
const theme = createTheme({
  typography: {
    fontSize: 12,
  },
  components: {
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "small",
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: "outlined",
        size: "small",
      },
    },
    MuiButton: {
      defaultProps: {
        size: "small",
      },
    },
  },
});

const AssetDetailsTable = () => {
  const { id } = useParams();

  const [assets, setAssets] = useState([]);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editedAsset, setEditedAsset] = useState(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newAsset, setNewAsset] = useState({
    title: "",
    width: "",
    height: "",
    latitude: "",
    longitude: "",
    description: "",
    structure: "",
    panels: 1, // Default panels to 1 for new assets
  });

  const [errors, setErrors] = useState({
    title: false,
    structure: false,
  });

  const [assetStructure, setAssetStructure] = useState([]);

  const getAllAssets = async (url) => {
    const res = await getSiteById(url);
    let temp = [];
    if (res.assetsDetails.length > 0) {
      for (let i = 0; i < res.assetsDetails.length; i++) {
        let obj = {
          AssetID: res.assetsDetails[i].AssetID,
          id: i + 1,
          title: res.assetsDetails[i].AssetType,
          width: res.assetsDetails[i].Width,
          height: res.assetsDetails[i].Height,
          panels: res.assetsDetails[i].panels || 1,
          latitude: res.assetsDetails[i].SiteDetail.LocationLat,
          longitude: res.assetsDetails[i].SiteDetail.LocationLong,
          description: "",
        };
        temp.push(obj);
      }
    }
    setAssets(temp);
  };

  const getAssetStructure = async () => {
    try {
      let structureResponse = await getAssetStructureDesign();
      // console.log("structureResponse", structureResponse);
      setAssetStructure(structureResponse);
    } catch (e) {
      console.log("error in getAssetStructure", getAssetStructure);
    } finally {
    }
  };

  React.useEffect(() => {
    getAssetStructure();
    getAllAssets(id);
  }, []);

  const handleOpenEditDialog = (asset) => {
    setEditedAsset(asset);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditedAsset(null);
  };

  const handleEditAsset = (updatedAsset) => {
    const newAssets = assets.map((asset) =>
      asset.id === editedAsset.id ? updatedAsset : asset
    );
    setAssets(newAssets);
    handleCloseEditDialog();
  };

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setNewAsset({
      title: "",
      width: "",
      height: "",
      latitude: "",
      longitude: "",
      description: "",
      panels: 1, // Reset panels to 1 when closing dialog
    });
  };

  const handleSaveNewAsset = async () => {
    try {
      if (!newAsset.title) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          title: "Title is required",
        }));
        return;
      }
      if (!newAsset.structure) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          structure: "Structure is required",
        }));
        return;
      }
      const area =
        Number(newAsset?.structure?.asset_height) *
        Number(newAsset?.structure?.asset_width) *
        Number(newAsset.panels);
      console.log("area", area);
      const postData = {
        AssetType: newAsset.title,
        Height: newAsset?.structure?.asset_height,
        Width: newAsset?.structure?.asset_width,
        panels: newAsset.panels, // Add panels to the payload
        DisplayArea: area,
        created_by: "user",
      };

      console.log("postData", postData);

      const addData = await createAssetBySiteId(id, postData);

      getAllAssets(id);
      handleCloseAddDialog();

      setOpenSnackBar(true);
      setSnackBarMessage({
        title: "Asset added successfully",
        type: "success",
      });
    } catch (error) {
      console.log("handleSaveNewAsset making error", error);
    }
    handleCloseAddDialog();
  };

  const handleDeleteAsset = async (idToDelete, assetId) => {
    try {
      const deletedAsset = await deleteAssetById(assetId);
      console.log("deletedStakeHolder", assetId);
      getAllAssets(id);

      setOpenSnackBar(true);
      setSnackBarMessage({
        title: "Asset Deleted Succesfully",
        type: "success",
      });
    } catch (error) {
      console.log("handleDeleteStakeholder making error", error);
    }
  };

  const calculateArea = (width, height, panels) => {
    if (width && height) {
      return parseFloat(width) * parseFloat(height) * parseInt(panels);
    }
    return "";
  };

  const totalArea = (assets) => {
    let summaryArea = 0;
    assets.forEach((element) => {
      summaryArea += calculateArea(
        +element.width,
        +element.height,
        +element.panels
      );
    });
    return summaryArea;
  };

  const handleChangeWithMandatory = (event) => {
    const { name, value, files } = event.target;
    // console.log("this is runing", value);
    setNewAsset((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
    validateFields(name, value, setErrors);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "AssetID",
        header: "Action",
        size: 50,
        Cell: ({ row }) => (
          <Typography
            sx={{
              cursor: "pointer",
              fontSize: "14px",
              color: "#1976D2",
              "&:hover": {
                color: "#1976D2",
              },
            }}
            onClick={() =>
              handleDeleteAsset(row.original.id, row.original.AssetID)
            }
          >
            <DeleteIcon sx={{ color: AppConfig.colorPalette.error.dark }} />
          </Typography>
        ),
      },

      {
        accessorKey: "id",
        header: "Serial No",
        size: 50,
      },
      {
        accessorKey: "title",
        header: "Title",
        size: 100,
      },
      {
        header: "Width * Height (ft)",
        accessorFn: (row) => `${row.width} x ${row.height}`,
        id: "widthHeight",
        size: 100,
      },
      {
        accessorKey: "panels",
        header: "Panels",
        size: 100,
      },
      {
        header: "Area (sq.ft)",
        accessorFn: (row) =>
          calculateArea(+row.width, +row.height, +row.panels),
        id: "areaSqFt",
        size: 100,
      },
    ],
    []
  );

  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState({
    title: "",
    type: "",
  });

  const handleCloseSnackBar = () => {
    setOpenSnackBar(false);
  };
  return (
    <ThemeProvider theme={theme}>
      <div style={{ width: "100%", margin: "auto" }}>
        {assets.length > 0 ? (
          <DynamicTable columns={columns} data={assets} />
        ) : (
          <Typography>
            No assets have been added yet. Please click here to add assets.
          </Typography>
        )}
        {/* Edit Dialog */}
        <Dialog open={openEditDialog} onClose={handleCloseEditDialog}>
          <DialogTitle>Edit Asset</DialogTitle>
          <DialogContent>
            <TextField
              margin="normal"
              fullWidth
              label="Title"
              value={editedAsset?.title || ""}
              onChange={(e) =>
                setEditedAsset((prev) => ({ ...prev, title: e.target.value }))
              }
            />
            <TextField
              margin="normal"
              fullWidth
              label="Width (ft)"
              type="number"
              value={editedAsset?.width || ""}
              onChange={(e) =>
                setEditedAsset((prev) => ({ ...prev, width: e.target.value }))
              }
            />
            <TextField
              margin="normal"
              fullWidth
              label="Height (ft)"
              type="number"
              value={editedAsset?.height || ""}
              onChange={(e) =>
                setEditedAsset((prev) => ({ ...prev, height: e.target.value }))
              }
            />
            <TextField
              margin="normal"
              fullWidth
              label="Latitude"
              value={editedAsset?.latitude || ""}
              onChange={(e) =>
                setEditedAsset((prev) => ({
                  ...prev,
                  latitude: e.target.value,
                }))
              }
            />
            <TextField
              margin="normal"
              fullWidth
              label="Longitude"
              value={editedAsset?.longitude || ""}
              onChange={(e) =>
                setEditedAsset((prev) => ({
                  ...prev,
                  longitude: e.target.value,
                }))
              }
            />
            <TextField
              margin="normal"
              fullWidth
              label="Description"
              value={editedAsset?.description || ""}
              onChange={(e) =>
                setEditedAsset((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog}>Cancel</Button>
            <Button
              onClick={() => handleEditAsset(editedAsset)}
              sx={{ color: "#b41660" }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add New Asset Dialog */}
        <Button
          onClick={handleOpenAddDialog}
          variant="contained"
          sx={{ margin: "16px 0", backgroundColor: "#b41660", color: "#fff" }}
        >
          Add New Asset
        </Button>
        <Dialog
          maxWidth="sm"
          open={openAddDialog}
          onClose={handleCloseAddDialog}
          fullWidth
        >
          <DialogTitle>Add New Asset</DialogTitle>
          <DialogContent>
            <Grid spacing={2}>
              <TextField
                margin="normal"
                fullWidth
                name="title"
                label={
                  <>
                    Title
                    <Typography
                      component="span"
                      sx={{ color: "red", marginLeft: "4px" }}
                    >
                      *
                    </Typography>
                  </>
                }
                value={newAsset.title}
                onChange={handleChangeWithMandatory}
                error={!!errors.title}
                helperText={errors.title}
              />

              <FormControl
                fullWidth
                // error={errors.remarkCategory}
              >
                <InputLabel>
                  Structure
                  <Typography
                    component="span"
                    sx={{ color: "red", marginLeft: "4px" }}
                  >
                    *
                  </Typography>
                </InputLabel>
                <Select
                  fullWidth
                  name="structure"
                  value={newAsset.structure}
                  onChange={handleChangeWithMandatory}
                  label="Structure"
                  sx={{
                    paddingBottom: "10px",
                    height: "3rem",
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 290,
                      },
                    },
                  }}
                >
                  {assetStructure?.map((structure, index) => {
                    return (
                      <MenuItem key={index} value={structure}>
                        {structure.StructureDesignName}
                      </MenuItem>
                    );
                  })}
                </Select>
                {errors.structure && (
                  <Typography variant="caption" color="error">
                    structure is required
                  </Typography>
                )}
              </FormControl>
              <Box mt={2}>
                {/* <TextField
                  label="Panels"
                  name="panels"
                  type="number"
                  value={newAsset.panels}
                  onChange={handleChangeWithMandatory}
                  fullWidth
                /> */}
                <TextField
                  label="Panels"
                  name="panels"
                  type="number"
                  value={newAsset.panels}
                  onChange={(event) => {
                    const value = event.target.value;

                    // Only update if the value is >= 1 and is not 0
                    if (value === "" || /^[1-9]\d*$/.test(value)) {
                      setNewAsset({
                        ...newAsset,
                        panels: value,
                      });
                    }
                  }}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
              </Box>
              <Box mt={2}>
                <Typography>
                  Structure Design: {newAsset.structure?.asset_width} W x{" "}
                  {newAsset.structure?.asset_height} H
                </Typography>
              </Box>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddDialog}>Cancel</Button>
            <Button onClick={handleSaveNewAsset} sx={{ color: "#b41660" }}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Box display="flex" flexDirection={"column"} gap={1} mt={4}>
          <Typography sx={{ fontWeight: 600 }}>
            Asset Summary (SqFt.)
          </Typography>
          <Typography sx={{ fontSize: "20px" }}>{totalArea(assets)}</Typography>
        </Box>
      </div>
      <SnackBars
        open={openSnackBar}
        setOpen={setOpenSnackBar}
        handleClose={handleCloseSnackBar}
        title={snackBarMessage.title}
        type={snackBarMessage.type}
      />
    </ThemeProvider>
  );
};

export default AssetDetailsTable;
